/**
 * Enterprise Configuration
 *
 * This is a static configuration file for instant domain-based branding.
 * It loads synchronously to avoid any flash of default branding.
 *
 * For new enterprises:
 * 1. Add their domains to ENTERPRISE_DOMAINS
 * 2. Add their full config to ENTERPRISE_CONFIGS
 * 3. Add their logo to assets/whitelabel/{key}/
 */

export interface EnterpriseConfig {
    key: string // Unique identifier (e.g., "parkupp", "redefine")
    name: string // Display name
    domain: string // Primary domain (e.g., "parkupp.co.za")
    isDefault: boolean // Only true for ParkUpp

    // Branding
    logoUrl: string
    primaryColor: string // Main brand color
    secondaryColor: string // Secondary/accent color (yellow for ParkUpp, dark grey for Redefine)
    headerColor: string // Header background color
    headerLight: boolean // true = light header (dark text), false = dark header (light text)
    bannerUrl?: string // Optional hero banner image
    faviconUrl?: string // Optional custom favicon

    // Content
    homepageHeading?: string
    homepageSubheading?: string
    footerText?: string
    pageTitle?: string // Browser tab title (e.g., "Redefine Parking Portal")

    // Contact - used for contact forms, contact page, parking page, etc.
    contactEmail?: string // Primary contact email (form submissions go here)
    contactPhone?: string // Primary contact phone

    // Email Settings (for backend reference)
    sendgridTemplateId?: string
    fromEmail?: string
    fromName?: string

    // Links
    termsUrl?: string
    privacyUrl?: string

    // UI Feature flags
    showWhatsAppButton?: boolean // Whether to show WhatsApp floating button (default: true for ParkUpp)

    // Business Settings
    defaultCommissionPercentage?: number // Default commission % for new merchants (15 for ParkUpp, 10 for Redefine)

    // Parking Type restrictions
    // If defined, only these parking types will be available for merchants
    // Uses ParkingType enum values: 'STANDARD', 'PARKING_COLLECTOR', 'FLEX', 'BOOKING', etc.
    allowedParkingTypes?: string[]
}

/**
 * Domain to Enterprise Key mapping
 * Supports full domains and subdomains
 */
export const ENTERPRISE_DOMAINS: { [domain: string]: string } = {
    // ParkUpp domains (default)
    'parkupp.co.za': 'parkupp',
    'www.parkupp.co.za': 'parkupp',
    'staging.parkupp.co.za': 'parkupp',
    localhost: 'parkupp',

    // Redefine domains
    'parking.redefine.co.za': 'redefine',
    'redefine.parkupp.co.za': 'redefine',
    'redefine.localhost': 'redefine', // Local development testing
    'redefine.localhost.co.za': 'redefine', // Local development testing
    
    // sslip.io domains for local development (Google OAuth compatible)
    'redefine.127.0.0.1.sslip.io': 'redefine',
    'redefine.192.168.1.155.sslip.io': 'redefine',

    // Add more enterprise domains here as needed
}

/**
 * Route-based enterprise mapping
 * For /enterprise/:key routes
 */
export const ENTERPRISE_ROUTE_KEYS: string[] = ['parkupp', 'redefine']

/**
 * Full Enterprise Configurations
 */
export const ENTERPRISE_CONFIGS: { [key: string]: EnterpriseConfig } = {
    parkupp: {
        key: 'parkupp',
        name: 'ParkUpp',
        domain: 'parkupp.co.za',
        isDefault: true,

        // Branding
        logoUrl: 'assets/images/branding/logo.svg',
        primaryColor: '#001533',
        secondaryColor: '#fdcf09', // ParkUpp yellow
        headerColor: '#001533',
        headerLight: false,

        // Content
        homepageHeading: 'Find Monthly Parking',
        homepageSubheading: 'Secure your parking bay today',
        footerText: '© ParkUpp. All rights reserved.',
        pageTitle: 'ParkUpp',

        // Contact
        contactEmail: 'info@parkupp.co.za',
        contactPhone: '+27 21 555 0123',

        // Email
        fromEmail: 'info@parkupp.co.za',
        fromName: 'ParkUpp',

        // UI
        showWhatsAppButton: true,

        // Business Settings
        defaultCommissionPercentage: 15,
    },

    redefine: {
        key: 'redefine',
        name: 'Redefine',
        domain: 'redefine.parkupp.co.za',
        isDefault: false,

        // Branding
        logoUrl: 'assets/whitelabel/redefine/logo.png',
        primaryColor: '#ed1c24',
        secondaryColor: '#333333', // Redefine dark grey
        headerColor: '#f7f7f7',
        headerLight: true,
        faviconUrl: 'assets/whitelabel/redefine/favicon.ico',

        // Content
        homepageHeading: 'Redefine Parking',
        homepageSubheading: 'Parking for people',
        footerText: '© Redefine Properties. Powered by ParkUpp.',
        pageTitle: 'Redefine Parking Portal',

        // Contact
        contactEmail: 'mark@parkupp.co.za',
        contactPhone: '+27 82 060 5355',

        // Email - can be customized per enterprise
        sendgridTemplateId: undefined, // Falls back to default
        fromEmail: 'mark@parkupp.co.za',
        fromName: 'Redefine Parking',

        // UI
        showWhatsAppButton: false, // No WhatsApp button for Redefine

        // Business Settings
        defaultCommissionPercentage: 10, // Redefine gets 10% commission

        // Parking Types - Redefine only allows Monthly and Allocate
        allowedParkingTypes: ['STANDARD', 'BOOKING'],
    },
}

/**
 * Get default enterprise config (ParkUpp)
 */
export function getDefaultEnterpriseConfig(): EnterpriseConfig {
    return ENTERPRISE_CONFIGS['parkupp']
}

/**
 * Get enterprise config by key
 */
export function getEnterpriseConfig(key: string): EnterpriseConfig {
    return ENTERPRISE_CONFIGS[key] || getDefaultEnterpriseConfig()
}

/**
 * Get enterprise key from hostname
 * This is called synchronously on app init for instant branding
 */
export function getEnterpriseKeyFromHostname(hostname: string): string {
    // Check for exact domain match
    if (ENTERPRISE_DOMAINS[hostname]) {
        return ENTERPRISE_DOMAINS[hostname]
    }

    // Check for subdomain match (e.g., redefine.parkupp.co.za)
    const parts = hostname.split('.')
    if (parts.length > 0) {
        const subdomain = parts[0]
        if (ENTERPRISE_ROUTE_KEYS.includes(subdomain)) {
            return subdomain
        }
    }

    // Default to ParkUpp
    return 'parkupp'
}

/**
 * Get enterprise key from URL path
 * Handles /enterprise/:key routes
 */
export function getEnterpriseKeyFromPath(pathname: string): string | null {
    const match = pathname.match(/^\/enterprise\/([^\/]+)/)
    if (match && ENTERPRISE_ROUTE_KEYS.includes(match[1])) {
        return match[1]
    }
    return null
}

/**
 * Detect enterprise from current browser context
 * Priority: 1. URL path (/enterprise/:key), 2. Domain
 */
export function detectEnterprise(): EnterpriseConfig {
    // Check URL path first (for /enterprise/:key routes)
    const pathKey = getEnterpriseKeyFromPath(window.location.pathname)
    if (pathKey) {
        return getEnterpriseConfig(pathKey)
    }

    // Fall back to domain-based detection
    const domainKey = getEnterpriseKeyFromHostname(window.location.hostname)
    return getEnterpriseConfig(domainKey)
}

/**
 * Check if current context is an enterprise (non-default)
 */
export function isEnterpriseContext(): boolean {
    const config = detectEnterprise()
    return !config.isDefault
}

/**
 * Get all enterprise configs (for admin dropdown)
 */
export function getAllEnterpriseConfigs(): EnterpriseConfig[] {
    return Object.values(ENTERPRISE_CONFIGS)
}

/**
 * Get enterprise display name by key
 */
export function getEnterpriseDisplayName(key: string): string {
    const config = ENTERPRISE_CONFIGS[key]
    return config ? config.name : key
}
